import type { BoardMoveCheck } from "./Board.types";
import type { BoardCellEntry } from "./boardCells";
import type { BoardDraggingState, BoardDragModel } from "./boardDragState";
import { sortOutGroup } from "./boardGroupMove";
import { getEntriesWithout, locateBoardEntry } from "./boardMove";

/**
 * The grabbed card, or the group's cards that land at the slot, with the
 * cell each would move to: the landings of a group drop placed by hand.
 */
const getCarriedMoves = <TItem>(
    { cells, cellOrder, canMove }: BoardDragModel<TItem>,
    { activeKey, group, target }: BoardDraggingState,
): Array<BoardMoveCheck<TItem>> => {
    if (group === null) {
        const location = locateBoardEntry(cells, activeKey);
        return location === null
            ? []
            : [
                  {
                      item: location.entry.item,
                      from: location.entry.placement,
                      to: target.placement,
                  },
              ];
    }
    return sortOutGroup({
        cells,
        cellOrder,
        group,
        activeKey,
        target,
        canMove,
    }).landings.map(({ from, to }) => ({
        item: from.item,
        from: from.placement,
        to,
    }));
};

/** The cards either side of the target slot once the carried cards are lifted out. */
const getSpotNeighbours = <TItem>(
    { cells }: BoardDragModel<TItem>,
    { activeKey, group, target }: BoardDraggingState,
    moves: ReadonlyArray<BoardMoveCheck<TItem>>,
) => {
    const entries = getEntriesWithout(cells, target.placement, activeKey);
    const movedItems = new Set(moves.map(({ item }) => item));
    const isStaying = ({ item }: BoardCellEntry<TItem>) =>
        group === null || !movedItems.has(item);
    return {
        beforeItem:
            entries.slice(0, target.index).reverse().find(isStaying)?.item ??
            null,
        afterItem: entries.slice(target.index).find(isStaying)?.item ?? null,
    };
};

/**
 * Why `canPlaceAt` refuses the drag's target slot: the first carried card
 * it refuses. Undefined when it allows every one, or isn't given.
 */
const getPlaceRefusal = <TItem>(
    model: BoardDragModel<TItem>,
    dragging: BoardDraggingState,
) => {
    const { canPlaceAt } = model;
    if (canPlaceAt === undefined) {
        return undefined;
    }
    const moves = getCarriedMoves(model, dragging);
    const neighbours = getSpotNeighbours(model, dragging, moves);
    for (const move of moves) {
        const verdict = canPlaceAt({ ...move, ...neighbours });
        if (!verdict.isAllowed) {
            return verdict.reason;
        }
    }
    return undefined;
};

/** The place key is held over a sorted board that accepts a position: its cells have slots. */
export const isPlaceHeldInSort = <TItem>(
    { compareItems, canPlaceBetween }: BoardDragModel<TItem>,
    { isPlaceHeld }: BoardDraggingState,
) => isPlaceHeld && canPlaceBetween && compareItems !== undefined;

/** Why a held drag can't place the card at its slot; undefined when it can, or isn't held. */
export const getHeldPlaceRefusal = <TItem>(
    model: BoardDragModel<TItem>,
    dragging: BoardDraggingState,
) =>
    isPlaceHeldInSort(model, dragging)
        ? getPlaceRefusal(model, dragging)
        : undefined;

/** A sorted board the user places the card on by hand, as in manual mode. */
export const isPlacingInSort = <TItem>(
    model: BoardDragModel<TItem>,
    dragging: BoardDraggingState,
) =>
    isPlaceHeldInSort(model, dragging) &&
    getPlaceRefusal(model, dragging) === undefined;
